import React from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibArrowRightIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import RhapHoverTooltip from '~/components/common/RhapHoverTooltip'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './SoapLinkButton.module.scss'

/**
 * Extract text from React children for aria-label
 */
const getChildrenText = (node: React.ReactNode): string => {
  if (typeof node === 'string') {
    return node
  }
  if (typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(getChildrenText).join('')
  }
  if (React.isValidElement(node) && node.props.children) {
    return getChildrenText(node.props.children)
  }
  return ''
}

interface SoapLinkButtonProps {
  children?: React.ReactNode
  className?: string
  onLinkClick?: () => void
  queryParams?: string
  soapId: string
}

/**
 * SOAP link button to open SOAP in a new tab via Agent Chat
 */
export const SoapLinkButton: React.FC<SoapLinkButtonProps> = ({
  soapId,
  children,
  className,
  onLinkClick,
  queryParams,
}) => {
  const { t } = useTranslation('Common')

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    onLinkClick?.()
    const url = queryParams
      ? `/soap/${soapId}?${queryParams}`
      : `/soap/${soapId}`
    window.open(url, '_blank')
  }

  const tooltipContent = t('Common:VIEW_SOAP')
  const ariaLabel = getChildrenText(children) || tooltipContent

  const buttonTrigger = (triggerProps: {
    'aria-describedby': string
    onBlur: () => void
    onFocus: () => void
    onMouseEnter: () => void
    onMouseLeave: () => void
    ref: React.Ref<HTMLElement>
  }) => (
    <span {...triggerProps} style={{ display: 'inline-block' }}>
      <KibButtonNew
        iconOnly
        aria-label={ariaLabel}
        className={classNames(styles.button, className)}
        emphasis="tertiary"
        icon={<KibArrowRightIcon />}
        size="small"
        theme="utility"
        onClick={handleClick}
      />
    </span>
  )

  return (
    <RhapHoverTooltip
      content={tooltipContent}
      trigger={buttonTrigger}
      variant="dark"
    />
  )
}
